import React, { useCallback, useContext, useState } from 'react';
import { Toast } from '../../components/Toast/Toast';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  text?: string;
  severity: Severity;
  autoHideDuration?: number | null;
  [key: string]: unknown;
}

interface ToastContextType {
  toast: boolean | null;
  toastProps: ToastProps;
  setToast: (showToast: boolean, props?: ToastProps) => void;
  successToast: (
    text: string,
    overrideToastProps?: Partial<ToastProps>
  ) => void;
  errorToast: (text: string, overrideToastProps?: Partial<ToastProps>) => void;
  infoToast: (text: string, overrideToastProps?: Partial<ToastProps>) => void;
  warningToast: (
    text: string,
    overrideToastProps?: Partial<ToastProps>
  ) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);
const { Provider } = ToastContext;

const toastTypes: Severity[] = ['success', 'error', 'info', 'warning'];

const useProvideToast = () => {
  const [toastState, setToastState] = useState<boolean | null>(null);
  const [toastProps, setToastProps] = useState<ToastProps>({
    severity: 'info',
    autoHideDuration: 5000,
  });

  const setToast = useCallback(
    (showToast: boolean, props: ToastProps = { severity: 'info' }) => {
      setToastState(showToast);
      setToastProps(props);
    },
    [setToastState, setToastProps]
  );

  const successToast = (
    text: string,
    overrideToastProps: Partial<ToastProps> = { autoHideDuration: 5000 }
  ) => {
    setToast(true, {
      text,
      severity: 'success',
      ...overrideToastProps,
    });
  };

  const errorToast = (
    text: string,
    overrideToastProps: Partial<ToastProps> = { autoHideDuration: 5000 }
  ) => {
    setToast(true, {
      text,
      severity: 'error',
      ...overrideToastProps,
    });
  };

  const infoToast = (
    text: string,
    overrideToastProps: Partial<ToastProps> = { autoHideDuration: 5000 }
  ) => {
    setToast(true, {
      text,
      severity: 'info',
      ...overrideToastProps,
    });
  };

  const warningToast = (
    text: string,
    overrideToastProps: Partial<ToastProps> = { autoHideDuration: 5000 }
  ) => {
    setToast(true, {
      text,
      severity: 'warning',
      ...overrideToastProps,
    });
  };

  return {
    toast: toastState,
    toastProps,
    setToast,
    toastTypes,
    successToast,
    errorToast,
    infoToast,
    warningToast,
  };
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const toastData = useProvideToast();

  return (
    <Provider value={toastData}>
      <Toast
        open={!!toastData.toast}
        handleClose={() => toastData.setToast(false)}
        {...toastData.toastProps}
      />
      {children}
    </Provider>
  );
};

export { ToastContext, ToastProvider };
